import { DataSource } from '@angular/cdk/collections';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, OnChanges } from '@angular/core';
import { System } from '@app/core/resources/system';
import { PlatformBrowserService } from '@app/core/services/platform-browser.service';
import { SystemLocalStorageService } from '@app/core/services/system-local-storage.service';
import { SystemsService } from '@app/core/services/systems.service';
import { NetworkService } from '@app/core/services/network.service';
import TransactionAmountCalculator from '@app/transactions/transaction-amount-calculator';
import { DocumentCollection } from 'ngx-jsonapi';
import { Observable, ReplaySubject, filter } from 'rxjs';
export interface PeriodicElement {
    system_id: string;
    buy: any;
    system: System;
    action: any;
    sell: any;
}

@Component({
    selector: 'app-comparison-table-system',
    templateUrl: './comparison-table-system.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./comparison-table-system.component.scss']
})
export class ComparisonTableSystemComponent implements OnChanges, AfterViewInit {
    @Input() public system: System = new System();
    @Input() public defaultSystems: Array<string> = [];
    @Input() public systemsKey: string = 'userSystem';
    public displayedColumns: Array<string> = ['system', 'buy', 'action', 'sell', 'more-info'];
    public dataToDisplay: Array<PeriodicElement> = [];
    public dataSource!: ComparisonTableDataSource;
    public systems: any = [];
    public objectSystems: any = {};
    public moreLinks: Array<{ [key: string]: string }> = [
        {
            title: 'Cotización histórica',
            url: '/cobrar/'
        },
        {
            title: 'Más información',
            url: '/comprar-vender/'
        }
    ];

    public constructor(
        private systemsService: SystemsService,
        private browserService: PlatformBrowserService,
        private systemLocalStorage: SystemLocalStorageService,
        public networkService: NetworkService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    public ngOnChanges(): void {
        if (!this.system.id) {
            return;
        }
        this.systemLocalStorage.setSystemKey(this.systemsKey).setSystemDefault(this.defaultSystems);
        this.getSystems();
    }

    public ngAfterViewInit(): void {
        if (!this.browserService.isBrowser) {
            return;
        }
        this.checkSizeScreen(window.innerWidth);
    }

    private checkSizeScreen(width: number): void {
        if (width < 700) {
            this.displayedColumns = ['system', 'action', 'more-info'];
        } else {
            this.displayedColumns = ['system', 'buy', 'action', 'sell', 'more-info'];
        }
    }

    @HostListener('window:resize', ['$event'])
    public setNavListItems(event: any): void {
        this.checkSizeScreen(event.target.innerWidth);
    }

    private getSystems(): void {
        this.systemsService
            .all({ include: ['rates', 'currency', 'system_information'] })
            .pipe(filter((systems): boolean => systems.source !== 'new'))
            .subscribe((systems: DocumentCollection<System>) => {
                if (!systems.builded) {
                    return;
                }

                this.systems = systems.data
                    .filter((system: System) => {
                        return system.id !== this.system.id && system.attributes.can_receive && system.attributes.can_send;
                    })
                    .forEach((element: any) => {
                        this.objectSystems[element.id] = element;
                    });
                this.dataToDisplay = [];
                this.setSystemDefault();
                this.dataSource = new ComparisonTableDataSource(this.dataToDisplay);
                this.changeDetectorRef.markForCheck();
            });
    }

    public removeSystemFromTable(system: string): void {
        this.systemLocalStorage.deleteSystem(system);
        this.removeSystemDataToDisplay(system);
        this.changeDetectorRef.markForCheck();
    }

    public setSystemDefault(): void {
        let auxSystem: Array<string> = this.systemLocalStorage.setSystemId(this.system.id).getSystem();
        for (const systemDefault of auxSystem) {
            this.dataToDisplay.push(this.getSystemDataByDisplay(this.objectSystems[systemDefault]));
        }
    }

    public removeSystemDataToDisplay(system: string): void {
        this.dataToDisplay = this.dataToDisplay.filter((systemId: any) => systemId.system_id !== system);
        this.dataSource.setData(this.dataToDisplay);
    }

    public addSystemDataToDisplay(): void {
        this.dataToDisplay.push(this.getSystemDataByDisplay(this.objectSystems.usdt));
        this.systemLocalStorage.addSystem(this.getSystemDataByDisplay(this.objectSystems.usdt).system_id);
        this.dataSource.setData(this.dataToDisplay);
    }

    public changeSystemAndCalculateSystem(system_id: string, new_system: System): void {
        let index: number = this.dataToDisplay.map((data: PeriodicElement) => data.system_id).indexOf(system_id);
        this.dataToDisplay[index] = this.getSystemDataByDisplay(new_system);
        this.systemLocalStorage.replaceSystem(this.getSystemDataByDisplay(new_system).system_id, index);
        this.dataSource.setData(this.dataToDisplay);
    }

    private getSystemDataByDisplay(systemData: System): PeriodicElement {
        return {
            system_id: systemData.id,
            system: systemData,
            buy: {
                amount: this.getAmountBuyOrSell('buy', this.system, systemData),
                currency: systemData.attributes.currency,
                time_average:
                    this.system.relationships.system_information.data[0] && this.system.relationships.system_information.data[0].attributes
                        ? this.system.relationships.system_information.data[0].attributes.time_average
                        : 0
            },
            action: {
                buy: '/b/' + systemData.id + '/' + this.system.id,
                sell: '/b/' + this.system.id + '/' + systemData.id
            },
            sell: {
                amount: this.getAmountBuyOrSell('sell', this.system, systemData),
                currency: systemData.attributes.currency,
                time_average:
                    systemData.relationships.system_information.data[0] && systemData.relationships.system_information.data[0].attributes
                        ? systemData.relationships.system_information.data[0].attributes.time_average
                        : 0
            }
        };
    }

    private getAmountBuyOrSell(operation: string, sendSystem: System, receiveSystem: System): string {
        let transactionAmountCalculator: TransactionAmountCalculator = new TransactionAmountCalculator();

        if (operation === 'buy') {
            return transactionAmountCalculator.getSendAmount(receiveSystem, sendSystem, 1).toFixed(receiveSystem.attributes.decimal_places);
        }

        return transactionAmountCalculator.getReceiveAmount(receiveSystem, sendSystem, 1).toFixed(receiveSystem.attributes.decimal_places);
    }

    public objectKeys(obj: any): Array<string> {
        return Object.keys(obj);
    }
}

class ComparisonTableDataSource extends DataSource<PeriodicElement> {
    private _dataStream: ReplaySubject<Array<PeriodicElement>> = new ReplaySubject<Array<PeriodicElement>>();

    public constructor(initialData: Array<PeriodicElement>) {
        super();
        this.setData(initialData);
    }

    public connect(): Observable<Array<PeriodicElement>> {
        return this._dataStream;
    }

    public disconnect(): void {
        // empty
    }

    public setData(data: Array<PeriodicElement>): void {
        this._dataStream.next(data);
    }
}
